/**
 * Admin → My sign-in security (STAFF_TELEGRAM_PLAN.md, part 4): how this staff login confirms its
 * sign-in. Two ways, the person's choice: the Telegram code from Joe IT Bot (linked in the CRM by
 * Support) or an authenticator app set up here. With both, they pick which comes first.
 */
import { useState } from 'react';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLoad } from '../stores/account';
import { TwoFactorCard } from './account/Security';

interface MySecurity {
  mode: 'off' | 'linked' | 'required';
  exempt: boolean;
  telegram: { state: 'no_crm_user' | 'linked' | 'blocked' | 'not_linked' | 'unknown'; username?: string };
  authenticator: { enabled: boolean; backupCodesLeft: number };
  preferred: 'telegram' | 'authenticator' | null;
}

const TELEGRAM: Record<MySecurity['telegram']['state'], [string, string, string]> = {
  linked: ['Linked', 'bg-up/15 text-up', 'Joe IT Bot sends your code to this Telegram.'],
  blocked: ['Bot blocked', 'bg-down/15 text-down', 'Joe IT Bot can’t message you. Open Joe IT Bot in Telegram and tap Start.'],
  not_linked: ['Not linked', 'bg-warn/15 text-warn', 'Ask Support to link your Telegram in the CRM (Settings → Agents → Security).'],
  no_crm_user: ['Not linked', 'bg-warn/15 text-warn', 'Your Platform login isn’t linked to your CRM user yet. Ask an admin (Staff & roles → Edit).'],
  unknown: ['Unknown', 'bg-raised text-muted', 'The CRM didn’t answer. Try again in a moment.'],
};

export function MySignInSecurityTab() {
  const me = useLoad<MySecurity>('/api/admin/my-security');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const d = me.data;
  const choose = async (preferred: 'telegram' | 'authenticator') => {
    setBusy(true);
    setMsg(null);
    try {
      await api.put('/api/admin/my-security', { preferred });
      await me.reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };
  const tg = d ? TELEGRAM[d.telegram.state] : null;
  const both = !!d && d.telegram.state === 'linked' && d.authenticator.enabled;
  const first = d?.preferred ?? 'telegram';
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <h2 className="m-0 text-lg font-semibold">How you confirm your sign-in</h2>
      <p className="m-0 text-sm text-muted">
        After your password, Platform can ask for a code. Set up at least one way: a Telegram code or an authenticator app. If you set up both, choose
        which one is used first; you can switch on the code screen.
      </p>
      {me.error && <p className="m-0 text-xs text-down">{me.error}</p>}
      {d?.exempt && <p className="m-0 rounded-lg bg-raised px-3 py-2 text-xs text-muted">Your login is exempt (STAFF_TG2FA_EXEMPT): Platform doesn’t ask you for a code.</p>}
      {d && tg && (
        <section className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="m-0 text-base font-semibold">Telegram code</h3>
            <span className={cx('inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold', tg[1])}>
              {d.telegram.state === 'linked' && d.telegram.username ? `@${d.telegram.username}` : tg[0]}
            </span>
          </div>
          <p className="m-0 text-sm text-muted">{tg[2]}</p>
        </section>
      )}
      {d && <TwoFactorCard staff onChange={() => void me.reload()} />}
      {both && (
        <section className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-4" aria-label="Use first">
          <h3 className="m-0 text-base font-semibold">Use first</h3>
          {(['telegram', 'authenticator'] as const).map((m) => (
            <label key={m} className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="radio" name="first" checked={first === m} disabled={busy} onChange={() => void choose(m)} />
              {m === 'telegram' ? 'Telegram code' : 'Authenticator app'}
            </label>
          ))}
          {busy && <Spinner />}
        </section>
      )}
      {msg && <p className="m-0 text-xs text-down">{msg}</p>}
    </div>
  );
}
